import { ComponentPropsWithoutRef, useContext } from "react";
import { styled } from "~/application/theme";
import { Icon } from "../Icon";
import { TabsContext } from "./contexts/TabsContext";
import { CSS } from "@stitches/react/types/css-util";

export type TabProps<TValue extends string | number = string> =
  ComponentPropsWithoutRef<"button"> & {
    value: TValue;
    css?: CSS;
  };

const TabRoot = styled("button", {
  // Reset
  all: "unset",
  boxSizing: "border-box",

  // Custom
  display: "inline-flex",
  alignItems: "center",
  gap: "$2",
  padding: "$4",
  background: "$neutrals-white",
  color: "$neutrals-dark",
  fill: "$neutrals-dark",
  fontSize: "$md",
  fontWeight: 500,
  transition: "$normal",
  userSelect: "none",

  "&:hover": {
    cursor: "pointer",
    background: "$primary-100",
    color: "$primary-700",
    fill: "$primary-700",
  },

  "&[data-active=true]": {
    backgroundColor: "$primary-600",
    color: "$neutrals-white",
    fill: "$neutrals-white",
    fontWeight: 700,
  },

  "&:disabled": {
    opacity: "$disabled",
    pointerEvents: "none",
  },

  [`& ${Icon}`]: {
    size: "$5",
  },

  "@mxlg": {
    color: "$primary-600",
    fontSize: "$sm",
    fontWeight: 400,
    "&[data-active=true]": {
      backgroundColor: "$primary-100",
      color: "$primary-600",
      fill: "$primary-600",
      fontWeight: 400,
    },
  },
});

export const Tab = <TValue extends string | number = string>({
  value,
  ...props
}: TabProps<TValue>) => {
  const { activeValue, setActiveValue } = useContext(TabsContext);

  return (
    <TabRoot
      role="tab"
      type="button"
      data-active={value === activeValue}
      onClick={() => setActiveValue(value)}
      {...props}
    />
  );
};

Tab.displayName = "Tab";
